import { c as _c } from "react/compiler-runtime";
import { HELD_SEAT_REASON, isSeatLocked, isSeatSoldOut } from "@components/table/members/assign-seats/assign-seats-utils";
import { SEAT_CARD_FRAME_SX, SeatCardContent } from "@components/table/members/assign-seats/seat-card-content";
import { SeatUsageText } from "@components/table/members/assign-seats/seat-usage-text";
import { Checkbox, ExclamationMarkCircleFillIcon, InfoCircleFillIcon, Text } from "@flat-ui";
import { Stack } from "@mui-wrapper";
/** The size of the icons in front of the status of a seat, matching the line of text they lead */
const STATUS_ICON_SX = {
  width: "1rem",
  height: "1rem",
  flexShrink: 0
};
/**
 * A status line of a seat, e.g. why it cannot be changed. It leads with an icon and is set in the
 * primary text color, so that it does not read as part of the description above it.
 */
function SeatStatus(t0) {
  const $ = _c(9);
  const {
    icon,
    color,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 0.5,
      alignItems: "center",
      mt: 0.5
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = color ?? "text.primary";
  let t3;
  if ($[1] !== t2) {
    t3 = {
      color: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <Text variant="bodyS" component="span" sx={t3}>{children}</Text>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== icon || $[7] !== t4) {
    t5 = <Stack direction="row" component="span" sx={t1}>{icon}{t4}</Stack>;
    $[6] = icon;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
/** Shows why a seat cannot be given to the member, or taken away from them */
function SeatCheckboxStatus(t0) {
  const $ = _c(9);
  const {
    option
  } = t0;
  if (!option.blockReason || option.blockReason === HELD_SEAT_REASON || isSeatSoldOut(option)) {
    return null;
  }
  const isError = option.blockSeverity === "error";
  const Icon = isError ? ExclamationMarkCircleFillIcon : InfoCircleFillIcon;
  const t1 = isError ? "error.main" : "text.secondary";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      ...STATUS_ICON_SX,
      color: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Icon || $[3] !== t2) {
    t3 = <Icon sx={t2} />;
    $[2] = Icon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isError ? "error.main" : undefined;
  let t5;
  if ($[5] !== option.blockReason || $[6] !== t3 || $[7] !== t4) {
    t5 = <SeatStatus icon={t3} color={t4}>{option.blockReason}</SeatStatus>;
    $[5] = option.blockReason;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

/**
 * A seat to select, shown as a card with its logo and what it lets the member do, and in its corner
 * whether the member holds it or what changing it does to the seats of the workspace. A checked
 * seat is outlined in the primary color. A seat whose state cannot be changed is shaded, and tells
 * why unless the member simply holds it already.
 */
export function SeatCheckbox(t0) {
  const $ = _c(28);
  const {
    option,
    isChecked,
    seatsAvailable,
    seatsUsed,
    isDisabled: t1,
    description,
    onChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== option) {
    t2 = isSeatLocked(option);
    $[0] = option;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isLocked = t2;
  const t3 = isDisabled || isLocked;
  let t4;
  if ($[2] !== isChecked || $[3] !== onChange || $[4] !== option.seat) {
    t4 = () => onChange(option.seat, !isChecked);
    $[2] = isChecked;
    $[3] = onChange;
    $[4] = option.seat;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isChecked ? "primary.main" : "divider";
  const t6 = isLocked ? "action.hover" : "transparent";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = {
      ...SEAT_CARD_FRAME_SX,
      alignItems: "flex-start",
      m: 0,
      borderColor: t5,
      backgroundColor: t6
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = isLocked && !option.isHeld;
  let t9;
  if ($[9] !== isChecked || $[10] !== option || $[11] !== seatsAvailable || $[12] !== seatsUsed) {
    t9 = <SeatUsageText option={option} isChecked={isChecked} seatsAvailable={seatsAvailable} seatsUsed={seatsUsed} />;
    $[9] = isChecked;
    $[10] = option;
    $[11] = seatsAvailable;
    $[12] = seatsUsed;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== option) {
    t10 = <SeatCheckboxStatus option={option} />;
    $[14] = option;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== description || $[17] !== option.seat || $[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = <SeatCardContent seat={option.seat} isCheckboxLabel={true} isLogoFaded={t8} description={description} corner={t9}>{t10}</SeatCardContent>;
    $[16] = description;
    $[17] = option.seat;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== isChecked || $[23] !== t11 || $[24] !== t3 || $[25] !== t4 || $[26] !== t7) {
    t12 = <Checkbox checked={isChecked} disabled={t3} onChange={t4} fullWidth={true} controlSx={t7} label={t11} />;
    $[22] = isChecked;
    $[23] = t11;
    $[24] = t3;
    $[25] = t4;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}